import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export default function AccountLocked() {
  const { logout } = useAuth();
  const [lockState, setLockState] = useState({
    admin_message:      '',
    admin_message_type: 'ERROR',
    action_required:    true,
  });
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ type: 'Account issue', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactResult, setContactResult] = useState(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('lock_state');
      if (stored) setLockState(JSON.parse(stored));
    } catch {}
  }, []);

  const handleSignOut = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleContactSubmit = async () => {
    if (!contactForm.message.trim()) return;
    setContactLoading(true);
    try {
      const API = (await import('./api')).default;
      const res = await API.post('/v1/support/tickets', {
        type:    contactForm.type,
        message: contactForm.message.trim(),
      });
      setContactResult({ success: true, ticket_id: res.data.ticket_id });
    } catch (err) {
      setContactResult({ success: false, error: err.response?.data?.error || 'Failed to submit. Please email support@visinvoke.com directly.' });
    } finally {
      setContactLoading(false);
    }
  };

  const messageType = lockState.admin_message_type || 'ERROR';
  const borderColor = messageType === 'ERROR' ? '#dc2626' : messageType === 'WARNING' ? '#f97316' : '#2563eb';
  const bgColor     = messageType === 'ERROR' ? '#fef2f2' : messageType === 'WARNING' ? '#fff7ed' : '#eff6ff';
  const labelColor  = messageType === 'ERROR' ? '#dc2626' : messageType === 'WARNING' ? '#f97316' : '#2563eb';

  return (
    <div style={s.page}>
      <div style={s.card}>

        <div style={{ ...s.iconWrap, borderColor, background: bgColor }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
            stroke={borderColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h1 style={s.heading}>Account Access Restricted</h1>
        <p style={s.subheading}>
          Your account requires attention before you can continue.
        </p>

        <div style={{ ...s.messageBox, borderColor, background: bgColor }}>
          <div style={{ ...s.messageLabel, color: labelColor }}>Account Notice</div>
          <div style={s.messageText}>
            {lockState.admin_message || 'Please contact support to restore your account access.'}
          </div>
        </div>

        <div style={s.divider} />

        {!showContact ? (
          <div style={s.actionsWrap}>
            <button style={{ ...s.primaryBtn, borderColor }} onClick={() => setShowContact(true)}>
              Contact Support
            </button>
            <button style={s.signOutBtn} onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        ) : (
          <div style={s.contactWrap}>
            {!contactResult ? (
              <>
                <div style={s.contactTitle}>Contact Support</div>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>Issue Type</label>
                  <select
                    style={s.fieldSelect}
                    value={contactForm.type}
                    onChange={e => setContactForm(f => ({ ...f, type: e.target.value }))}
                  >
                    <option>Account issue</option>
                    <option>Billing issue</option>
                    <option>General enquiry</option>
                    <option>Other</option>
                  </select>
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>Message</label>
                  <textarea
                    style={s.fieldTextarea}
                    value={contactForm.message}
                    onChange={e => setContactForm(f => ({ ...f, message: e.target.value.slice(0, 1000) }))}
                    placeholder="Describe your situation..."
                    rows={4}
                  />
                  <div style={s.charCount}>{contactForm.message.length}/1000</div>
                </div>
                <button
                  style={{ ...s.primaryBtn, borderColor, opacity: contactLoading ? 0.6 : 1 }}
                  onClick={handleContactSubmit}
                  disabled={contactLoading || !contactForm.message.trim()}
                >
                  {contactLoading ? 'Submitting...' : 'Submit Request'}
                </button>
                <button style={s.cancelBtn} onClick={() => setShowContact(false)}>
                  Cancel
                </button>
              </>
            ) : contactResult.success ? (
              <div style={s.successBox}>
                <div style={s.successTitle}>Request Submitted</div>
                <div style={s.successText}>
                  Your support request has been received.{' '}
                  {contactResult.ticket_id && <strong>Ticket ID: {contactResult.ticket_id}</strong>}
                </div>
                <div style={s.successText}>We will respond to your registered email address.</div>
                <button style={s.signOutBtn} onClick={handleSignOut}>Sign Out</button>
              </div>
            ) : (
              <div style={s.errorBox}>
                <div style={s.errorText}>{contactResult.error}</div>
                <button style={s.cancelBtn} onClick={() => setContactResult(null)}>Try Again</button>
              </div>
            )}
          </div>
        )}

        <p style={s.footer}>
          Your account data is retained. Contact{' '}
          <a href="mailto:support@visinvoke.com" style={s.footerLink}>support@visinvoke.com</a>
          {' '}if you need further assistance.
        </p>

      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Segoe UI', sans-serif", padding: '32px 16px' },
  card: { background: '#ffffff', borderRadius: 20, padding: '48px 44px', maxWidth: 500, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0 },
  iconWrap: { width: 76, height: 76, borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24, flexShrink: 0 },
  heading: { fontSize: 26, fontWeight: 800, color: '#111827', margin: '0 0 10px' },
  subheading: { fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' },
  messageBox: { width: '100%', border: '1px solid', borderRadius: 10, padding: '16px 20px', textAlign: 'left', marginBottom: 24, boxSizing: 'border-box' },
  messageLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
  messageText: { fontSize: 15, color: '#374151', lineHeight: 1.55, fontWeight: 500 },
  divider: { width: '100%', height: 1, background: '#f3f4f6', marginBottom: 24 },
  actionsWrap: { display: 'flex', flexDirection: 'column', gap: 12, width: '100%', marginBottom: 24 },
  primaryBtn: { background: '#fff', border: '2px solid', borderRadius: 10, padding: '13px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', transition: 'opacity 0.2s' },
  signOutBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '11px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' },
  contactWrap: { width: '100%', textAlign: 'left', marginBottom: 24 },
  contactTitle: { fontWeight: 800, fontSize: 16, color: '#111827', marginBottom: 16 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: '#374151' },
  fieldSelect: { border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#1a1a1a', background: '#fff' },
  fieldTextarea: { border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#1a1a1a', resize: 'vertical', fontFamily: 'inherit' },
  charCount: { fontSize: 11, color: '#9ca3af', textAlign: 'right' },
  cancelBtn: { background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: '8px 0', width: '100%', marginTop: 4 },
  successBox: { display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', textAlign: 'center', padding: '8px 0' },
  successTitle: { fontWeight: 800, fontSize: 16, color: '#16a34a' },
  successText: { fontSize: 14, color: '#374151', lineHeight: 1.55 },
  errorBox: { display: 'flex', flexDirection: 'column', gap: 10 },
  errorText: { fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' },
  footer: { fontSize: 12, color: '#9ca3af', margin: '8px 0 0', lineHeight: 1.6 },
  footerLink: { color: '#7c3aed', textDecoration: 'none', fontWeight: 600 },
};
